import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserCheck, Users, FileText, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import useAuthStore from '../../context/authStore';
import api from '../../utils/api';

const statusInfo = {
  PENDING:           { label: 'Registration Pending',        color: '#92400e', bg: '#fef3c7', icon: Clock },
  REFERRAL_PENDING:  { label: 'Awaiting Referral Approvals', color: '#1e40af', bg: '#dbeafe', icon: Clock },
  REFERRAL_APPROVED: { label: 'Referrals Approved – Awaiting Admin', color: '#5b21b6', bg: '#ede9fe', icon: CheckCircle },
  PAYMENT_PENDING:   { label: 'Payment Verification Pending', color: '#92400e', bg: '#fef3c7', icon: Clock },
  PAYMENT_RECEIVED:  { label: 'Payment Received – Activation Pending', color: '#065f46', bg: '#d1fae5', icon: CheckCircle },
  ACTIVE:            { label: 'Active Member',               color: '#14532d', bg: '#dcfce7', icon: CheckCircle },
  REJECTED:          { label: 'Application Rejected',        color: '#7f1d1d', bg: '#fee2e2', icon: AlertCircle },
};

export default function MemberDashboard() {
  const { user } = useAuthStore();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    api.get('/member/referral-requests').then((res) => {
      setPendingCount(res.data.requests?.length || 0);
    }).catch(() => {});
  }, []);

  const status = user?.memberStatus ? statusInfo[user.memberStatus] : null;
  const StatusIcon = status?.icon;

  return (
    <div>
      <div className="page-header">
        <h2>Dashboard</h2>
        <p>Welcome to the Bombay Psychiatric Society Member Portal</p>
      </div>

      {/* Status Banner */}
      {!user?.hasProfile && (
        <div style={{
          background: 'linear-gradient(135deg, var(--primary), var(--primary-light))',
          borderRadius: 'var(--radius-lg)', padding: '28px 32px', color: 'white', marginBottom: 28
        }}>
          <h3 style={{ marginBottom: 8, fontSize: 20 }}>Complete Your Membership Registration</h3>
          <p style={{ opacity: 0.85, marginBottom: 18, fontSize: 14 }}>
            You've created your account. Now fill in your membership details and select your two referrals to begin the approval process.
          </p>
          <Link to="/register-membership" className="btn btn-accent">
            Start Membership Registration →
          </Link>
        </div>
      )}

      {status && (
        <div style={{
          background: status.bg, border: `1px solid ${status.color}30`,
          borderRadius: 'var(--radius-lg)', padding: '20px 24px',
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28
        }}>
          <StatusIcon size={24} style={{ color: status.color, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, color: status.color }}>{status.label}</div>
            {user.memberId && (
              <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
                Member ID: <strong>{user.memberId}</strong>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Action Cards */}
      <div className="grid-3" style={{ marginBottom: 28 }}>
        <div className="card" style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 6 }}>Pending Referral Requests</div>
              <div style={{ fontSize: 36, fontWeight: 700, color: 'var(--primary)', fontFamily: "'Playfair Display', serif" }}>
                {pendingCount}
              </div>
            </div>
            <div style={{ background: '#dbeafe', borderRadius: 10, padding: 10 }}>
              <UserCheck size={22} style={{ color: 'var(--primary-light)' }} />
            </div>
          </div>
          <Link to="/referral-requests" style={{ fontSize: 13, color: 'var(--primary-light)', textDecoration: 'none', marginTop: 12, display: 'block' }}>
            Review requests →
          </Link>
        </div>

        <div className="card" style={{ padding: 24, opacity: user?.isActive ? 1 : 0.6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 6 }}>Member Directory</div>
              <div style={{ fontSize: 14, color: user?.isActive ? 'var(--success)' : 'var(--text-muted)', fontWeight: 500, marginTop: 6 }}>
                {user?.isActive ? '✓ Access Available' : 'Activation Required'}
              </div>
            </div>
            <div style={{ background: '#dcfce7', borderRadius: 10, padding: 10 }}>
              <Users size={22} style={{ color: 'var(--success)' }} />
            </div>
          </div>
          {user?.isActive
            ? <Link to="/directory" style={{ fontSize: 13, color: 'var(--primary-light)', textDecoration: 'none', marginTop: 12, display: 'block' }}>Browse directory →</Link>
            : <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 12 }}>Available after account activation</div>
          }
        </div>

        <div className="card" style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 6 }}>My Profile</div>
              <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500, marginTop: 6 }}>
                {user?.hasProfile ? 'Profile Complete' : 'Not Submitted'}
              </div>
            </div>
            <div style={{ background: '#fef3c7', borderRadius: 10, padding: 10 }}>
              <FileText size={22} style={{ color: '#92400e' }} />
            </div>
          </div>
          <Link to="/profile" style={{ fontSize: 13, color: 'var(--primary-light)', textDecoration: 'none', marginTop: 12, display: 'block' }}>
            View profile →
          </Link>
        </div>
      </div>

      {/* How It Works */}
      {!user?.isActive && (
        <div className="card" style={{ padding: 28 }}>
          <h3 style={{ color: 'var(--primary)', marginBottom: 20, fontSize: 18 }}>Membership Activation Steps</h3>
          <div style={{ display: 'flex', gap: 0, position: 'relative' }}>
            {[
              { step: '1', title: 'Sign Up', desc: 'Create your account with basic details', done: true },
              { step: '2', title: 'Submit Registration', desc: 'Fill membership form with two referrals', done: user?.hasProfile },
              { step: '3', title: 'Referral Approvals', desc: 'Both referrals approve your request', done: ['REFERRAL_APPROVED','PAYMENT_RECEIVED','ACTIVE'].includes(user?.memberStatus) },
              { step: '4', title: 'Payment & Activation', desc: 'Admin records payment and activates your account', done: user?.memberStatus === 'ACTIVE' },
            ].map((item, idx) => (
              <div key={idx} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
                {idx < 3 && <div style={{ position: 'absolute', top: 20, left: '60%', width: '80%', height: 2, background: item.done ? 'var(--primary)' : 'var(--border)', zIndex: 0 }} />}
                <div style={{
                  width: 42, height: 42, borderRadius: '50%',
                  background: item.done ? 'var(--primary)' : 'var(--border)',
                  color: item.done ? 'white' : 'var(--text-muted)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 16, margin: '0 auto 10px', position: 'relative', zIndex: 1
                }}>
                  {item.done ? '✓' : item.step}
                </div>
                <div style={{ fontWeight: 600, fontSize: 13, color: item.done ? 'var(--primary)' : 'var(--text)' }}>{item.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
